'use client';

/**
 * WhatsAppCompose - Message composition area for WhatsApp sending
 *
 * Reusable compose section that appears at the bottom of chat components.
 * Handles message input, file attachments (paperclip → base64 chips),
 * send button state, and loading indicators.
 *
 * Attachment props (`attachments` + `onAttachmentsChange`) are optional —
 * when omitted the attachment row is hidden, preserving the original
 * text-only contract for any caller not yet wired.
 *
 * @component_role Message Composition
 * @business_rule Send button disabled when no recipient selected, message empty,
 *   or send in progress. Shows loading spinner during send.
 * @user_outcome Clear compose experience - type message, attach file, hit send.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { Phone, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import type { WhatsAppMediaAttachment } from '@/lib/api/types';
import { WhatsAppAttachmentRow } from './whatsapp-attachment-row';

interface WhatsAppComposeProps {
  message: string;
  onMessageChange: (message: string) => void;
  onSend: () => void;
  canSend: boolean;
  isSending: boolean;
  recipientLabel?: string;
  placeholder?: string;
  attachments?: WhatsAppMediaAttachment[];
  onAttachmentsChange?: (next: WhatsAppMediaAttachment[]) => void;
}

export function WhatsAppCompose({
  message,
  onMessageChange,
  onSend,
  canSend,
  isSending,
  recipientLabel,
  placeholder = 'Type a message...',
  attachments,
  onAttachmentsChange,
}: WhatsAppComposeProps) {
  return (
    <div className="p-3 border-t border-panel-border space-y-2 shrink-0">
      <div className="flex items-center gap-2">
        <Phone className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">
          {recipientLabel || 'Send WhatsApp'}
        </span>
      </div>

      {attachments && onAttachmentsChange && (
        <WhatsAppAttachmentRow
          attachments={attachments}
          onAttachmentsChange={onAttachmentsChange}
          disabled={isSending}
        />
      )}

      <div className="relative">
        <Textarea
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          placeholder={placeholder}
          className="min-h-[60px] pr-10 resize-none text-sm"
        />
        <Button
          size="icon"
          className="absolute bottom-2 right-2 h-7 w-7"
          disabled={!canSend}
          onClick={onSend}
        >
          {isSending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// Extended version with manual phone input support
// ============================================================

interface WhatsAppComposeWithManualProps extends Omit<WhatsAppComposeProps, 'recipientLabel'> {
  /** Currently selected contact name (for display) */
  selectedContactName?: string | null;
  /** Whether manual entry mode is active */
  showManualEntry: boolean;
  /** Manual phone number value */
  manualPhone: string;
  /** Handler for manual phone changes */
  onManualPhoneChange: (phone: string) => void;
  /** Handler to exit manual entry mode */
  onCancelManualEntry: () => void;
  /** Warning message (e.g., "This contact has no phone number") */
  warning?: string;
  /** Children for contact selector slot */
  children?: React.ReactNode;
}

export function WhatsAppComposeWithContactSelector({
  message,
  onMessageChange,
  onSend,
  canSend,
  isSending,
  selectedContactName,
  showManualEntry,
  manualPhone,
  onManualPhoneChange,
  onCancelManualEntry,
  warning,
  placeholder = 'Type a message...',
  children,
  attachments,
  onAttachmentsChange,
}: WhatsAppComposeWithManualProps) {
  return (
    <div className="p-3 border-t border-panel-border space-y-2 shrink-0">
      <div className="flex items-center gap-2">
        <Phone className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">Send WhatsApp</span>
      </div>

      {/* Contact Selection / Manual Entry */}
      {!showManualEntry ? (
        <>
          {children}
          {warning && <p className="text-xs text-warning">{warning}</p>}
        </>
      ) : (
        <div className="flex items-center gap-2">
          <Input
            value={manualPhone}
            onChange={(e) => onManualPhoneChange(e.target.value)}
            placeholder="Phone (+61...)"
            className="h-8 text-sm flex-1"
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={onCancelManualEntry}
          >
            Cancel
          </Button>
        </div>
      )}

      {attachments && onAttachmentsChange && (
        <WhatsAppAttachmentRow
          attachments={attachments}
          onAttachmentsChange={onAttachmentsChange}
          disabled={isSending}
        />
      )}

      {/* Message Input */}
      <div className="relative">
        <Textarea
          value={message}
          onChange={(e) => onMessageChange(e.target.value)}
          placeholder={placeholder}
          className="min-h-[60px] pr-10 resize-none text-sm"
        />
        <Button
          size="icon"
          className="absolute bottom-2 right-2 h-7 w-7"
          disabled={!canSend}
          onClick={onSend}
        >
          {isSending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
